import { MeshGroup, StateMachineType } from "@/3d/meshGroup/MeshGroup";
import { useEditorState } from "@/state/editorState";
import { Vector3 } from "three";
import React, { useMemo } from "react";

type MeshGroupGeneralSettingsProps = {
    group: MeshGroup;
    onChange: (group: MeshGroup) => void;
};

type StateMachineTypeOption = {
    type?: StateMachineType;
    label: string;
};

const StateMachineTypeOptions: ReadonlyArray<StateMachineTypeOption> = [
    {
        label: "None",
    },
    {
        type: "leftDoor",
        label: "Left Door",
    },
    {
        type: "rightDoor",
        label: "Right Door",
    },
];
export const MeshGroupGeneralSettings = ({ group, onChange }: MeshGroupGeneralSettingsProps) => {
    const model = useEditorState((state) => state.model);
    const meshGroups = useEditorState((state) => state.meshGroups);

    const availableMeshes = useMemo(() => {
        if (!model) return [];

        // Get all meshes that are already in other groups
        const usedMeshes = new Set(
            Object.values(meshGroups)
                .filter((g) => g.id !== group.id)
                .flatMap((g) => g.meshNames)
        );

        // Return only meshes not used in other groups
        return model.meshes.map((mesh) => mesh.name).filter((name) => !usedMeshes.has(name));
    }, [model, meshGroups, group.id]);

    const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        onChange({
            ...group,
            name: e.target.value,
        });
    };

    const handleMeshToggle = (meshName: string) => {
        const newMeshNames = group.meshNames.includes(meshName)
            ? group.meshNames.filter((name) => name !== meshName)
            : [...group.meshNames, meshName];

        // Initialize position and rotation for new meshes
        const newMeshPositions = { ...group.meshPositions };
        const newMeshRotations = { ...group.meshRotations };

        if (!group.meshNames.includes(meshName)) {
            newMeshPositions[meshName] = new Vector3(0, 0, 0);
            newMeshRotations[meshName] = new Vector3(0, 0, 0);
        }

        onChange({
            ...group,
            meshNames: newMeshNames,
            meshPositions: newMeshPositions,
            meshRotations: newMeshRotations,
        });
    };

    const handlePositionChange = (axis: "x" | "y" | "z", value: number) => {
        const newPosition = new Vector3().copy(group.position);
        newPosition[axis] = value;
        onChange({
            ...group,
            position: newPosition,
        });
    };

    const handleRotationChange = (axis: "x" | "y" | "z", value: number) => {
        const newRotation = new Vector3().copy(group.rotation);
        newRotation[axis] = value;
        onChange({
            ...group,
            rotation: newRotation,
        });
    };

    const handleStateMachineTypeChange = (stateMachineType: StateMachineType | undefined) => {
        onChange({
            ...group,
            stateMachineType,
        });
    };

    return (
        <div className="container p-3">
            <div className="mb-3">
                <label className="form-label">Name</label>
                <input
                    type="text"
                    value={group.name}
                    onChange={handleNameChange}
                    className="form-control"
                />
            </div>

            <div className="mb-3">
                <label className="form-label">Meshes</label>
                <div className="mt-2">
                    {availableMeshes.map((meshName) => (
                        <div key={meshName} className="form-check">
                            <input
                                type="checkbox"
                                checked={group.meshNames.includes(meshName)}
                                onChange={() => handleMeshToggle(meshName)}
                                className="form-check-input"
                            />
                            <label className="form-check-label">{meshName}</label>
                        </div>
                    ))}
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label">Animation type</label>
                <div className="mt-2">
                    <select
                        className="form-select"
                        value={group.stateMachineType}
                        onChange={(e) =>
                            handleStateMachineTypeChange(e.target.value as StateMachineType)
                        }
                    >
                        {StateMachineTypeOptions.map((option) => (
                            <option key={option.type ?? "none"} value={option.type}>
                                {option.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label">Group Position</label>
                <div className="row g-2">
                    {(["x", "y", "z"] as const).map((axis) => (
                        <div key={axis} className="col-4">
                            <label className="form-label text-muted small">
                                {axis.toUpperCase()}
                            </label>
                            <input
                                type="number"
                                value={group.position[axis]}
                                onChange={(e) =>
                                    handlePositionChange(axis, parseFloat(e.target.value) || 0)
                                }
                                className="form-control"
                            />
                        </div>
                    ))}
                </div>
            </div>

            <div className="mb-3">
                <label className="form-label">Group Rotation</label>
                <div className="row g-2">
                    {(["x", "y", "z"] as const).map((axis) => (
                        <div key={axis} className="col-4">
                            <label className="form-label text-muted small">
                                {axis.toUpperCase()}
                            </label>
                            <input
                                type="number"
                                value={group.rotation[axis] ?? 0}
                                onChange={(e) =>
                                    handleRotationChange(axis, parseFloat(e.target.value) || 0)
                                }
                                className="form-control"
                            />
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};
